import { sinVerComo } from "@/lib/impersonation/ver-como-lectura";
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { activityLoggerService } from "@/services/activityLoggerService";
import { useInactivityTimeout } from "@/hooks/useInactivityTimeout";
import { limpiarTodosLosFiltros } from "@/lib/filtrosPersistentes";
import { vieneDeFlujoConfirmacion } from "@/lib/emailConfirmacion";
import { esSesionMuertaEnServidor } from "@/lib/sesionServidor";
import {
  TIMEOUT_INTENTO_PERFIL_MS,
  backoffPerfilMs,
  clasificarErrorPerfil,
  conTimeout,
  decidirTrasFalloPerfil,
  requiereRefrescarSesion,
  type FalloPerfil,
} from "@/lib/perfilSesion";

/** Marca de "ya se recargó una vez por no poder leer la sesión" (evita un bucle de recargas). */
const CLAVE_RECARGA_SESION = "sozu-auth-recarga-sesion";

interface UserProfile {
  email: string;
  nombre: string;
  rol_id: number;
  rol_nombre: string;
  debe_cambiar_password: boolean;
  /**
   * Persona EFECTIVA de la sesión: la que la cuenta representa con el rol activo
   * (`user_roles.id_persona_contexto` del rol activo, p. ej. la empresa en el rol
   * Inmobiliaria) o, si no hay contexto, la propia (`usuarios.id_persona`).
   */
  id_persona: number | null;
  /**
   * Persona propia de la cuenta (`usuarios.id_persona`), sin importar el rol
   * activo. Solo la trae la RPC nueva; con una BD vieja se rellena con
   * `id_persona` (allí ambas coinciden).
   */
  id_persona_cuenta?: number | null;
  activo: boolean;
  ver_todos_prospectos_compradores: boolean;
  ver_filtros_avanzados_eliminados: boolean;
  id_notario: number | null;
  notaria_nombre: string | null;
  id_banco: number | null;
  banco_nombre: string | null;
  id_perfil_juridico: number | null;
  puede_impersonar: boolean;
  administrar_app_clientes: boolean;
  foto_perfil_url: string | null;
  frase_perfil: string | null;
  /** usuarios.email_confirmado — si el correo ya fue verificado. */
  email_confirmado: boolean;
  /** roles.requiere_confirmacion_email — true solo en roles de portal/externos. */
  requiere_confirmacion_email: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  isLoading: boolean;
  /** True mientras get_current_user_profile está en vuelo (incluye reintentos y su backoff). */
  isProfileLoading: boolean;
  mustChangePassword: boolean;
  permissionVersion: number; // Incremented when permissions change
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  refreshProfile: () => Promise<void>;
  triggerPermissionRefresh: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Cierre por inactividad: 60 minutos sin actividad en NINGUNA pestaña del admin.
const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000;

// Todos los signOut del admin son `scope: "local"`. El default de supabase-js es "global", que
// revoca TODAS las sesiones del usuario en el servidor: un cierre por inactividad aquí sacaba al
// mismo usuario de la app de clientes, la de agentes y otros navegadores donde seguía trabajando.

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [permissionVersion, setPermissionVersion] = useState(0);
  const realtimeChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  // Usuario dueño de la sesión vigente. Es la referencia contra la que
  // fetchProfile decide si su resultado todavía sirve: una consulta lanzada para
  // el usuario A no debe escribir el perfil cuando ya hay sesión de B (login con
  // huella/autocompletado tarda menos que el fetch, así que la carrera es real y
  // deja "usuario nuevo con rol viejo" — de ahí las pantallas de "sin acceso a
  // este portal" contra cuentas que sí tienen acceso).
  const sesionActivaRef = useRef<string | null>(null);
  // Número de la última llamada a fetchProfile. Solo la más reciente escribe el
  // perfil: al cambiar de rol (seleccionar_rol_activo) el fetch que disparó el
  // SIGNED_IN puede resolver DESPUÉS del refresh posterior a la RPC y dejaría el
  // perfil con el rol anterior.
  const fetchProfileSeqRef = useRef(0);
  // Espejo del perfil vigente y de a qué usuario pertenece. fetchProfile lo usa
  // para no tirar un perfil bueno de ESTA sesión por un fallo de red al releerlo.
  const profileRef = useRef<UserProfile | null>(null);
  const perfilDuenoRef = useRef<string | null>(null);
  // True mientras hay una carga de perfil en vuelo (incluido su backoff). Evita
  // que TOKEN_REFRESHED / foco disparen otra encima: el refreshSession del propio
  // reintento emite TOKEN_REFRESHED y reiniciaría el contador sin fin.
  const perfilEnVueloRef = useRef(false);
  useEffect(() => {
    profileRef.current = profile;
    if (!profile) perfilDuenoRef.current = null;
  }, [profile]);

  // Antes del siguiente intento: confirma que sigue habiendo sesión y, si el
  // fallo fue de token, la refresca. Nunca se llama dentro de onAuthStateChange.
  const prepararSesionParaReintento = useCallback(async (clase: FalloPerfil["clase"]) => {
    try {
      const { data } = await conTimeout(supabase.auth.getSession(), TIMEOUT_INTENTO_PERFIL_MS);
      const sesion = data.session;
      // Sin sesión no hay nada que refrescar: el SIGNED_OUT del listener limpia.
      if (!sesion) return;
      const venceProntoMs = (sesion.expires_at ?? Infinity) * 1000 - Date.now();
      if (clase === "sesion" || venceProntoMs < 60000) {
        await conTimeout(supabase.auth.refreshSession(), TIMEOUT_INTENTO_PERFIL_MS);
      }
    } catch (e) {
      console.warn("No se pudo preparar la sesión para reintentar el perfil:", e);
    }
  }, []);

  const fetchProfile = useCallback(async (usuarioEsperado?: string | null) => {
    const dueno = usuarioEsperado ?? sesionActivaRef.current;
    const seq = ++fetchProfileSeqRef.current;
    // El resultado solo se aplica si la sesión sigue siendo la misma que al
    // arrancar y ninguna llamada posterior lo reemplazó; si no, se descarta en
    // silencio (el fetch vigente ya viene en camino).
    const sigueVigente = () =>
      sesionActivaRef.current === dueno && fetchProfileSeqRef.current === seq;

    setIsProfileLoading(true);
    perfilEnVueloRef.current = true;
    try {
      // Reintentos con backoff ante fallos de red/5xx/token (ver
      // src/lib/perfilSesion.ts). Antes cualquier fallo, incluso de una relectura
      // en segundo plano, dejaba profile = null y la pantalla de error.
      let data: unknown[] | null = null;
      for (let intento = 1; ; intento += 1) {
        if (!sigueVigente()) return;

        let clase: FalloPerfil["clase"] | null = null;
        try {
          const res = await conTimeout(
            supabase.rpc("get_current_user_profile"),
            TIMEOUT_INTENTO_PERFIL_MS,
          );
          if (res.error) {
            clase = clasificarErrorPerfil(res.error);
            console.warn(`Error fetching profile (intento ${intento}, ${clase}):`, res.error);
          } else if (Array.isArray(res.data) && res.data.length > 0) {
            data = res.data;
          } else {
            clase = "sin_filas";
          }
        } catch (err) {
          clase = clasificarErrorPerfil(err);
          console.warn(`Error in fetchProfile (intento ${intento}, ${clase}):`, err);
        }

        if (!sigueVigente()) return;
        if (data) break;

        const accion = decidirTrasFalloPerfil({
          clase: clase ?? "transitorio",
          intentosHechos: intento,
          hayPerfilPrevio: profileRef.current !== null && perfilDuenoRef.current === dueno,
        });
        if (accion === "reintentar") {
          await new Promise((r) => setTimeout(r, backoffPerfilMs(intento - 1)));
          if (!sigueVigente()) return;
          if (requiereRefrescarSesion(clase ?? "transitorio")) {
            await prepararSesionParaReintento(clase ?? "transitorio");
          }
          continue;
        }
        if (accion === "error") {
          console.error("No se pudo cargar el perfil tras reintentar:", clase);
          setProfile(null);
        }
        // "conservar": el perfil de esta sesión sigue en pantalla.
        return;
      }

      if (!sigueVigente()) return;

      if (data && data.length > 0) {
        const row = data[0] as Partial<UserProfile>;
        // La RPC nueva devuelve email_confirmado / requiere_confirmacion_email.
        // Mientras la migración no esté desplegada esas columnas llegan undefined:
        // los defaults tolerantes (confirmado / no requiere confirmación) evitan
        // que el gate bloquee a nadie contra una BD vieja.
        const base: UserProfile = {
          ...(row as UserProfile),
          id_persona_cuenta: row.id_persona_cuenta !== undefined ? row.id_persona_cuenta : (row.id_persona ?? null),
          email_confirmado: row.email_confirmado ?? true,
          requiere_confirmacion_email: row.requiere_confirmacion_email ?? false,
        };

        // Reparación de la divergencia auth.users.email_confirmed_at (no nulo)
        // vs usuarios.email_confirmado (false). `mark_email_confirmed` sube la
        // bandera y sigue haciendo falta para los usuarios migrados en masa, que
        // llegan confirmados en Auth pero con la columna en false.
        //
        // Se ejecuta DESPUÉS de leer el perfil y solo en los casos en que no
        // anula el gate de EmailNoConfirmado. Antes corría siempre y dos líneas
        // antes del SELECT: borraba la única divergencia que el gate podía
        // detectar, así que la pantalla nunca se pintaba.
        //   · rol interno (requiere_confirmacion_email = false): la bandera es
        //     puro dato heredado, no hay gate que proteger → se repara.
        //   · rol de portal que ACABA de confirmar por enlace en esta pestaña:
        //     la confirmación es real y la columna aún no se propagó → se repara.
        //   · rol de portal sin rastro de confirmación: NO se toca. Si la bandera
        //     está en false ahí es señal legítima y el gate debe verla.
        const puedeRepararBandera =
          !base.email_confirmado &&
          (!base.requiere_confirmacion_email || vieneDeFlujoConfirmacion(base.email));
        if (puedeRepararBandera) {
          try {
            await conTimeout(supabase.rpc("mark_email_confirmed"), TIMEOUT_INTENTO_PERFIL_MS);
          } catch (e) {
            console.error("Error marking email confirmed:", e);
          }
        }

        // La RPC get_current_user_profile no devuelve la foto/frase de perfil;
        // se traen por separado desde usuarios para el avatar del header.
        let foto_perfil_url: string | null = null;
        let frase_perfil: string | null = null;
        let email_confirmado = base.email_confirmado;
        try {
          // La fila propia, con la sesión real aunque haya "ver como".
          const { data: u, error: errFoto } = await conTimeout(sinVerComo((supabase as any)
            .from("usuarios")
            .select("foto_perfil_url, frase_perfil, email_confirmado")
            .eq("email", base.email))
            .maybeSingle(), TIMEOUT_INTENTO_PERFIL_MS) as { data: any; error: unknown };
          if (errFoto) throw errFoto;
          foto_perfil_url = u?.foto_perfil_url ?? null;
          frase_perfil = u?.frase_perfil ?? null;
          // Releer la bandera aquí evita un round-trip extra: dice si la
          // reparación realmente prosperó (la RPC no hace nada cuando Auth
          // tampoco tiene el correo confirmado).
          if (puedeRepararBandera && typeof u?.email_confirmado === "boolean") {
            email_confirmado = u.email_confirmado;
          }
        } catch (e) {
          console.error("Error fetching perfil foto:", e);
          // Un fallo transitorio (token por refrescar, red) no debe borrar la foto que ya se veía:
          // se conserva la del perfil vigente de este mismo usuario.
          const previo = profileRef.current;
          if (previo && perfilDuenoRef.current === dueno) {
            foto_perfil_url = previo.foto_perfil_url;
            frase_perfil = previo.frase_perfil;
          }
        }
        if (sigueVigente()) {
          perfilDuenoRef.current = dueno;
          setProfile({ ...base, email_confirmado, foto_perfil_url, frase_perfil });
        }
      }
    } catch (err) {
      // Solo llega aquí un fallo inesperado del post-proceso (los de la RPC se
      // manejan en el bucle). Mismo criterio: no tirar un perfil vigente.
      console.error("Error in fetchProfile:", err);
      if (sigueVigente() && !(profileRef.current && perfilDuenoRef.current === dueno)) {
        setProfile(null);
      }
    } finally {
      if (fetchProfileSeqRef.current === seq) perfilEnVueloRef.current = false;
      if (sigueVigente()) setIsProfileLoading(false);
    }
  }, [prepararSesionParaReintento]);

  const triggerPermissionRefresh = useCallback(() => {
    setPermissionVersion((v) => v + 1);
  }, []);

  const handleForceLogout = useCallback(async (motivo?: string) => {
    // Clean up realtime channel
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
      realtimeChannelRef.current = null;
    }
    await supabase.auth.signOut({ scope: "local" });
    sesionActivaRef.current = null;
    setUser(null);
    setSession(null);
    setProfile(null);
    // Force reload to ensure clean state
    window.location.href = motivo ? `/auth/login?reason=${motivo}` : "/auth/login";
  }, []);

  // Validación de la sesión contra el servidor.
  //
  // `getSession()` solo lee el token guardado y `getUser()` es la única llamada
  // que confirma que la fila de `auth.sessions` sigue viva. Sin esta validación
  // una sesión cerrada en otra pestaña (o revocada) deja al usuario en un limbo:
  // PostgREST acepta el JWT por firma y las pantallas cargan, pero cada Edge
  // Function que llama `auth.getUser()` responde 401 y la UI solo puede mostrar
  // "Edge Function returned a non-2xx status code". Se ejecuta al montar con
  // sesión y al volver el foco, con throttle para no pegarle a /auth/v1/user en
  // cada cambio de pestaña.
  const ultimaValidacionSesionRef = useRef<number>(0);
  const validarSesionEnServidor = useCallback(async () => {
    const ahora = Date.now();
    if (ahora - ultimaValidacionSesionRef.current < 60000) return;
    ultimaValidacionSesionRef.current = ahora;

    const { error } = await supabase.auth.getUser();
    if (!error) return;

    if (esSesionMuertaEnServidor(error)) {
      console.warn("Sesión inválida en el servidor, cerrando sesión:", error.message);
      await handleForceLogout("sesion-invalida");
      return;
    }
    // Fallo de red o 5xx: no se toca la sesión, el token local sigue sirviendo.
    console.warn("No se pudo validar la sesión contra el servidor:", error.message);
  }, [handleForceLogout]);

  // Visibility change: refresh permissions when tab becomes visible (throttled 30s)
  const lastVisibilityRefreshRef = useRef<number>(0);
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && user && profile) {
        const now = Date.now();
        if (now - lastVisibilityRefreshRef.current > 30000) {
          lastVisibilityRefreshRef.current = now;
          triggerPermissionRefresh();
        }
        // La pestaña que estuvo en segundo plano es justo la que se queda con un
        // token cuya sesión ya cerró otra pestaña.
        void validarSesionEnServidor();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [user, profile, triggerPermissionRefresh, validarSesionEnServidor]);

  // Sesión viva pero sin perfil (la carga se rindió): al volver el foco, la
  // visibilidad o la red, se reintenta solo, sin esperar a que el usuario pulse
  // "Reintentar". Con perfil cargado no hace nada: esa relectura ya la dispara
  // supabase-js con el SIGNED_IN de _recoverAndRefresh.
  useEffect(() => {
    if (!user || profile) return;
    const reintentarSiFalta = () => {
      if (document.visibilityState !== "visible") return;
      if (profileRef.current || perfilEnVueloRef.current) return;
      if (!sesionActivaRef.current) return;
      void fetchProfile();
    };
    document.addEventListener("visibilitychange", reintentarSiFalta);
    window.addEventListener("focus", reintentarSiFalta);
    window.addEventListener("online", reintentarSiFalta);
    return () => {
      document.removeEventListener("visibilitychange", reintentarSiFalta);
      window.removeEventListener("focus", reintentarSiFalta);
      window.removeEventListener("online", reintentarSiFalta);
    };
  }, [user, profile, fetchProfile]);

  // Primera validación en cuanto hay sesión: cubre el caso de abrir/recargar la
  // app con un token guardado cuya sesión ya no existe.
  useEffect(() => {
    if (!user) return;
    void validarSesionEnServidor();
  }, [user, validarSesionEnServidor]);

  // Set up realtime subscriptions for permission changes
  useEffect(() => {
    if (!user || !profile) return;

    const userEmail = user.email;
    const rolId = profile.rol_id;

    // Clean up existing channel if any
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
    }

    // Create a single channel for all permission-related subscriptions
    const channel = supabase.channel("auth-permission-updates");

    // 1. Subscribe to user status changes (activo field)
    channel.on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "usuarios",
        filter: `email=eq.${userEmail}`,
      },
      (payload) => {
        const newRecord = payload.new as { activo?: boolean; rol_id?: number };

        // If user was deactivated, force logout
        if (newRecord.activo === false) {
          console.log("User deactivated, forcing logout");
          handleForceLogout();
          return;
        }

        // If role changed, refresh profile and permissions
        if (newRecord.rol_id !== rolId) {
          console.log("User role changed, refreshing permissions");
          fetchProfile();
          triggerPermissionRefresh();
        }
      },
    );

    // 2. Subscribe to role permission changes (submenus_permisos)
    channel.on(
      "postgres_changes",
      {
        event: "*", // INSERT, UPDATE, DELETE
        schema: "public",
        table: "submenus_permisos",
        filter: `rol_id=eq.${rolId}`,
      },
      () => {
        console.log("Role permissions changed, refreshing");
        triggerPermissionRefresh();
      },
    );

    // 3. Subscribe to role configuration changes
    channel.on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "roles",
        filter: `id=eq.${rolId}`,
      },
      () => {
        console.log("Role configuration changed, refreshing");
        fetchProfile();
        triggerPermissionRefresh();
      },
    );

    // 4. Subscribe to project access changes for this user
    if (userEmail) {
      channel.on(
        "postgres_changes",
        {
          event: "*", // INSERT, UPDATE, DELETE
          schema: "public",
          table: "proyectos_acceso",
          filter: `usuario_id=eq.${userEmail}`,
        },
        () => {
          console.log("Project access changed, refreshing");
          triggerPermissionRefresh();
        },
      );
    }

    // Subscribe to the channel
    channel.subscribe((status) => {
      if (status === "SUBSCRIBED") {
        console.log("Real-time permission subscriptions active");
      }
    });

    realtimeChannelRef.current = channel;

    return () => {
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
    };
  }, [user, profile?.rol_id, profile?.email, fetchProfile, triggerPermissionRefresh, handleForceLogout]);

  useEffect(() => {
    let isMounted = true;
    let currentUserId: string | null = null;

    // Carga del perfil FUERA del callback de onAuthStateChange. supabase-js
    // ejecuta el callback con el lock de auth tomado (lo espera dentro de
    // _notifyAllSubscribers) y cualquier llamada que pida sesión desde ahí
    // —rpc/from llaman getSession por dentro— queda encolada en ese lock. El
    // setTimeout(0) suelta el callback primero (patrón documentado por Supabase).
    // isProfileLoading se marca ya, en el mismo render que setUser, para que
    // ProtectedRoute no pinte "No pudimos cargar tu perfil" durante ese tick.
    const programarCargaPerfil = (userId: string) => {
      setIsProfileLoading(true);
      setTimeout(() => {
        if (!isMounted || sesionActivaRef.current !== userId) return;
        void fetchProfile(userId).finally(() => {
          if (isMounted && sesionActivaRef.current === userId) {
            setIsLoading(false);
          }
        });
      }, 0);
    };

    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!isMounted) return;

      // Si es solo un refresh de token y el usuario es el mismo, solo actualizar sesión
      // Esto evita re-cargar el perfil innecesariamente al cambiar de pestaña.
      // Excepción: si el perfil no está (falló con el token anterior) y no hay
      // una carga en curso, el token nuevo es justo lo que faltaba.
      if (event === "TOKEN_REFRESHED" && currentUserId && newSession?.user?.id === currentUserId) {
        setSession(newSession);
        if (!profileRef.current && !perfilEnVueloRef.current) {
          programarCargaPerfil(currentUserId);
        }
        return;
      }

      const usuarioAnterior = currentUserId;
      currentUserId = newSession?.user?.id ?? null;
      sesionActivaRef.current = currentUserId;
      setSession(newSession);
      setUser(newSession?.user ?? null);

      if (newSession?.user) {
        // Cambio de cuenta en la misma pestaña: el perfil anterior NO puede
        // sobrevivir al cambio. Si se queda, los gates de permisos deciden con el
        // rol del usuario que se fue (y como useAllowedMenus ya cargó una vez, no
        // muestra spinner: decide con datos ajenos en lugar de esperar).
        if (usuarioAnterior && usuarioAnterior !== newSession.user.id) {
          setProfile(null);
          setIsLoading(true);
        }
        programarCargaPerfil(newSession.user.id);
      } else {
        setProfile(null);
        setIsProfileLoading(false);
        setIsLoading(false);
      }
    });

    // THEN check for existing session. Con varias pestañas del admin abiertas (Safari las
    // restaura juntas y congela las de fondo) el candado de auth se roba entre pestañas:
    // getSession() puede rechazar o no volver nunca, y sin salida la pantalla se quedaba en
    // "Cargando..." para siempre. Si falla, el cliente de auth queda así toda la vida de la
    // página, así que se recarga una vez; si vuelve a fallar, se manda al login.
    conTimeout(supabase.auth.getSession(), TIMEOUT_INTENTO_PERFIL_MS).then(({ data: { session } }) => {
      if (!isMounted) return;
      try { sessionStorage.removeItem(CLAVE_RECARGA_SESION); } catch { /* sin storage */ }

      // Si el listener ya registró una sesión (login rápido con huella/
      // autocompletado que llega antes que getSession), esta respuesta es vieja:
      // no debe pisar al usuario vigente.
      if (sesionActivaRef.current && sesionActivaRef.current !== (session?.user?.id ?? null)) {
        return;
      }

      currentUserId = session?.user?.id ?? null;
      sesionActivaRef.current = currentUserId;
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        fetchProfile(session.user.id).finally(() => {
          if (isMounted && sesionActivaRef.current === session.user.id) {
            setIsLoading(false);
          }
        });
      } else {
        setIsLoading(false);
      }
    }).catch((e) => {
      console.error("No se pudo leer la sesión al iniciar:", e);
      if (!isMounted || sesionActivaRef.current) return;
      let yaRecargo = true;
      try {
        yaRecargo = sessionStorage.getItem(CLAVE_RECARGA_SESION) === "1";
        if (!yaRecargo) sessionStorage.setItem(CLAVE_RECARGA_SESION, "1");
      } catch { /* sin storage: no se arriesga un bucle de recargas */ }
      if (!yaRecargo) window.location.reload();
      else setIsLoading(false);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Registrar intento fallido
        activityLoggerService.registrarInicioSesion(email, "error", error.message);
        return { error };
      }

      // Registrar inicio de sesión exitoso
      activityLoggerService.registrarInicioSesion(email, "exito");

      // fetchProfile decide si toca reparar usuarios.email_confirmado
      // (ver el bloque `puedeRepararBandera`); no hay que llamarlo aquí.

      return { error: null };
    } catch (err) {
      activityLoggerService.registrarInicioSesion(email, "error", (err as Error).message);
      return { error: err as Error };
    }
  };

  const signOut = async () => {
    const userEmail = profile?.email || user?.email || "desconocido";
    // Se espera antes de cerrar la sesión: después el insert sale como anon y RLS lo rechaza (401).
    await activityLoggerService.registrarCierreSesion(userEmail);

    // Clean up realtime channel
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
      realtimeChannelRef.current = null;
    }

    await supabase.auth.signOut({ scope: "local" });
    limpiarTodosLosFiltros(); // los filtros de tabla no deben sobrevivir a un cambio de usuario
    sesionActivaRef.current = null;
    setUser(null);
    setSession(null);
    setProfile(null);
  };

  // Handle inactivity logout
  const handleInactivityTimeout = useCallback(async () => {
    console.log("Session expired due to inactivity");
    try {
      // Clean up realtime channel
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
      await supabase.auth.signOut({ scope: "local" });
    } catch (err) {
      console.error("Error during inactivity signOut:", err);
    }
    limpiarTodosLosFiltros();
    // Siempre redirigir, sin importar si signOut falló
    window.location.href = "/auth/login?reason=inactivity";
  }, []);

  // Auto-logout after inactivity - only active when user is logged in
  useInactivityTimeout({
    timeoutMs: INACTIVITY_TIMEOUT_MS,
    onTimeout: handleInactivityTimeout,
    enabled: !!user && !isLoading,
  });

  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        return { error };
      }

      // Mark password as changed in usuarios table
      await supabase.rpc("mark_password_changed");

      // Refresh profile to get updated debe_cambiar_password
      await fetchProfile();

      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const refreshProfile = async () => {
    await fetchProfile();
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    isLoading,
    isProfileLoading,
    mustChangePassword: profile?.debe_cambiar_password ?? false,
    permissionVersion,
    signIn,
    signOut,
    updatePassword,
    refreshProfile,
    triggerPermissionRefresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
